import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import {
  getPercentileColor,
  getPercentileLabel,
  getPercentileTooltip,
} from "@/services/metricsService";
import { MetricGroupData, PercentileInfo } from "@/types/metrics";

import { MetricTooltip } from "./MetricTooltip";

interface MetricsAccordionProps {
  groups: MetricGroupData[];
  highlightedMetrics: string[]; // metric_names that should be highlighted
  percentileData?: Record<string, PercentileInfo> | null;
  className?: string;
}

/** Color-coded legend for percentile ranking tiers. */
function PercentileLegend() {
  const tiers = [
    { label: "Top 25%", color: getPercentileColor(80, true) },
    { label: "Above avg", color: getPercentileColor(60, true) },
    { label: "Below avg", color: getPercentileColor(40, true) },
    { label: "Bottom 25%", color: getPercentileColor(10, true) },
  ];

  return (
    <div className="flex items-center gap-4 px-4 py-2 text-xs text-gray-500 border-b bg-gray-50/50">
      <span className="font-medium text-gray-400">Ranking:</span>
      {tiers.map((tier) => (
        <span key={tier.label} className="flex items-center gap-1">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: tier.color }}
          />
          {tier.label}
        </span>
      ))}
    </div>
  );
}

export function MetricsAccordion({
  groups,
  highlightedMetrics,
  percentileData,
  className,
}: MetricsAccordionProps) {
  // Track which groups the user has explicitly toggled
  const [userToggled, setUserToggled] = useState<Record<string, boolean>>({});

  // A group is expanded if: user explicitly set it, OR (no user toggle AND it has highlighted metrics)
  const isExpanded = (group: string): boolean => {
    if (group in userToggled) return !!userToggled[group];
    return groups
      .find((g) => g.group === group)
      ?.metrics.some((m) => highlightedMetrics.includes(m.metricName)) ?? false;
  };

  const toggleGroup = (group: string) => {
    setUserToggled((prev) => ({ ...prev, [group]: !isExpanded(group) }));
  };

  const hasPercentiles =
    percentileData != null && Object.keys(percentileData).length > 0;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Percentile legend — only when percentile data is present */}
      {hasPercentiles && <PercentileLegend />}

      {groups.map((groupData) => {
        const expanded = isExpanded(groupData.group);
        const hasHighlighted = groupData.metrics.some((m) =>
          highlightedMetrics.includes(m.metricName)
        );

        return (
          <div
            key={groupData.group}
            className="border rounded-lg overflow-hidden bg-surface"
          >
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(groupData.group)}
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 text-left",
                "hover:bg-gray-50 transition-colors",
                hasHighlighted && "bg-blue-50"
              )}
            >
              <div className="flex items-center gap-2">
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
                <span className="font-medium text-gray-900">
                  {groupData.group}
                </span>
                <span className="text-sm text-gray-500">
                  ({groupData.metrics.length} metrics)
                </span>
              </div>
            </button>

            {/* Group Content */}
            {expanded && (
              <div className="border-t divide-y">
                {groupData.metrics.map((metric) => {
                  const isHighlighted = highlightedMetrics.includes(
                    metric.metricName
                  );
                  const pData = percentileData?.[metric.metricName];

                  return (
                    <div
                      key={metric.metricName}
                      className={cn(
                        "flex items-center justify-between px-4 py-2",
                        isHighlighted && "bg-blue-50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <MetricTooltip tooltip={metric.tooltipCopy}>
                          <span
                            className={cn(
                              "text-sm cursor-help",
                              isHighlighted
                                ? "font-medium text-gray-900"
                                : "text-gray-600"
                            )}
                          >
                            {metric.displayName}
                          </span>
                        </MetricTooltip>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Inline percentile bar with hover tooltip */}
                        {pData?.percentile != null && (
                          <PercentileBar pData={pData} />
                        )}

                        <span
                          className={cn(
                            "text-sm tabular-nums",
                            isHighlighted
                              ? "font-semibold text-gray-900"
                              : "text-gray-700"
                          )}
                        >
                          {metric.formattedValue}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Percentile bar with human-readable label and hover tooltip. */
function PercentileBar({ pData }: { pData: PercentileInfo }) {
  const label = getPercentileLabel(pData.percentile, pData.higherIsBetter);
  const tooltip = getPercentileTooltip(
    pData.percentile,
    pData.higherIsBetter,
    pData.sampleSize
  );
  const barColor = getPercentileColor(pData.percentile, pData.higherIsBetter);

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 w-[120px] cursor-help">
            <div className="relative flex-1 h-1.5 bg-gray-100 rounded-full">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pData.percentile}%`,
                  backgroundColor: barColor,
                }}
              />
              {/* Marker dot */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white shadow-sm"
                style={{
                  left: `${Math.min(Math.max(pData.percentile, 5), 95)}%`,
                  transform: "translateX(-50%) translateY(-50%)",
                  backgroundColor: barColor,
                }}
              />
            </div>
            <span
              className="text-[10px] tabular-nums text-right whitespace-nowrap"
              style={{ color: barColor }}
            >
              {label}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs p-2.5 space-y-1">
          <p className="text-xs font-medium text-gray-100">{tooltip.rank}</p>
          <p className="text-xs text-gray-300">{tooltip.context}</p>
          <p className="text-[10px] text-gray-400">{tooltip.basis}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
